import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { business } from "../siteData";

/*
  What the visitor sees after they submit.

  The old version was one green sentence under a form that stayed on screen, and
  it read as uncertain because it gave the visitor nothing they could check. The
  shipping products that get this right all hand back a record instead:

  - Walmart Auto Care ends its booking on a receipt: a check, the appointment,
    the vehicle, the location, contact details, and the line "A confirmation has
    been sent to <the address they typed>".
  - Jobber, which is the software this category actually runs on, confirms with
    "Request submitted. We'll be in touch soon." and then shows the request as a
    dated card the client can look at.
  - Rivian gives every service request an appointment number.

  The thin ones look like ours did. Pipedrive's is "Your message is sent",
  Bonsai's is "Your response was submitted successfully", Superhuman's is "Thanks
  for reaching out". None of them tell you where it went, what you asked for, or
  what happens now, which is exactly the doubt being reported here.

  So this screen answers four questions, in this order: did it send, where did the
  confirmation go, what did I ask for, and what happens next.
*/

// Per request type: the headline, and the sequence the customer should expect.
// No timing is stated that Prime Path Motors has not agreed to.
const COPY = {
  service: {
    heading: "Your appointment request is in",
    lede: "This is a request, not a booked slot. It goes on the schedule once someone at Prime Path Motors confirms it with you.",
    next: [
      "Someone reads your request and checks the bay schedule for that day and time.",
      "They call or email you to confirm the slot, or to offer the nearest one that works.",
      "Once you have that confirmation, your appointment is on the board.",
    ],
  },
  tire: {
    heading: "Your tire quote request is in",
    lede: "We match the fitment for your exact vehicle before we quote, so the number you get is the number for your truck or car, not a shelf price.",
    next: [
      "We look up the fitments for the vehicle you gave us.",
      "You get options with clear pricing: good, better, best.",
      "If you want them fitted, we arrange the installation with the quote.",
    ],
  },
  vehicle: {
    heading: "Your vehicle request is in",
    lede: "Your budget is the brief. We source against the number you set, and you see the full cost in writing before you commit to anything.",
    next: [
      "We search our nationwide network against what you asked for.",
      "Every candidate is inspected against our standards before you see it.",
      "You get complete details, the inspection report, and clear pricing, then you decide.",
    ],
  },
  appraisal: {
    heading: "Your appraisal request is in",
    lede: "We look at the actual vehicle, its mileage and its condition before we give you a number, so what you get is one we can stand behind.",
    next: [
      "We review the details and photos you sent.",
      "We come back with a number, and what it is based on.",
      "If you are sourcing a vehicle with us, your trade gets worked into the deal.",
    ],
  },
  general: {
    heading: "Your message is in",
    lede: "It reached the right inbox and the right person picks it up.",
    next: [
      "Someone reads your message and works out who should answer it.",
      "They reply using the contact details you gave us.",
    ],
  },
};

// Fields worth reading back, per type, in the order a person would say them.
// Anything not listed here (consent, honeypot) is not repeated at the customer.
const ECHO = {
  service: [["preferredDate", "Requested date"], ["preferredTime", "Requested time"], ["vehicle", "Vehicle"], ["service", "Service"]],
  tire: [["vehicle", "Vehicle"], ["tireSize", "Tire size"], ["drivingType", "Driving"]],
  vehicle: [["vehicleType", "Looking for"], ["budget", "Budget"], ["tradeIn", "Trade-in"]],
  appraisal: [["vehicle", "Vehicle"], ["mileage", "Mileage"], ["condition", "Condition"]],
  general: [["topic", "About"]],
};

export default function RequestReceipt({ type, reference, values, onReset }) {
  const copy = COPY[type] || COPY.general;
  const echo = (ECHO[type] || []).filter(([key]) => values[key]);
  const headingRef = useRef(null);

  // The submit button sits well below the top of the form, so replacing the form
  // in place leaves the confirmation above the fold and the visitor looking at
  // the spot where a button used to be. That is its own reason to doubt a
  // request went through. Focus also fell to <body>, so a keyboard or screen
  // reader user was left with nothing at all. Move both to the heading.
  useEffect(() => {
    const node = headingRef.current;
    if (!node) return;
    node.focus({ preventScroll: true });
    node.scrollIntoView({ block: "center", behavior: "instant" });
  }, []);

  return (
    <section className="receipt" aria-live="polite">
      <div className="receipt-head">
        <span className="receipt-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <div>
          <h2 ref={headingRef} tabIndex={-1}>{copy.heading}</h2>
          {reference ? <p className="receipt-ref">Reference <b>{reference}</b></p> : null}
        </div>
      </div>

      <p className="receipt-lede">{copy.lede}</p>

      {/* Naming the exact address they typed is the single most reassuring line
          on the screen, and it catches a typo while they are still here. */}
      <div className="receipt-sent">
        <p>A confirmation is on its way to <b>{values.email}</b>.</p>
        <p className="receipt-spam">
          If it is not there within a few minutes, look in your spam or promotions folder and mark it
          as not spam. That keeps our reply to your request out of there too.
        </p>
      </div>

      {echo.length ? (
        <div className="receipt-block">
          <p className="receipt-label">What you asked for</p>
          <dl className="receipt-list">
            {echo.map(([key, label]) => (
              <React.Fragment key={key}>
                <dt>{label}</dt>
                <dd>{values[key]}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      ) : null}

      <div className="receipt-block">
        <p className="receipt-label">What happens next</p>
        <ol className="receipt-steps">
          {copy.next.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </div>

      <div className="receipt-actions">
        <a className="button button-dark" href={`tel:${business.phoneHref}`}>Call {business.phoneDisplay}</a>
        <Link className="button button-outline-dark" to="/">Back to home</Link>
        {onReset ? (
          <button className="receipt-again" type="button" onClick={onReset}>Send another request</button>
        ) : null}
      </div>
    </section>
  );
}
